import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, Package, AlertTriangle } from 'lucide-react';
import { ItemStockSummaryResponse } from '@/app/types/inventory';
import { Item } from '@/app/types/item';

interface StockSummaryCardProps {
  stockSummary?: ItemStockSummaryResponse;
  item?: Item;
  isLoading: boolean;
  error?: any;
}

export function StockSummaryCard({ stockSummary, item, isLoading, error }: StockSummaryCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load stock information: {error.message}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const totalStock = stockSummary ? parseFloat(stockSummary.total_stock) : 0;
  const isLowStock = item?.min_stock_level && totalStock < parseFloat(item.min_stock_level);
  const isOverStock = item?.max_stock_level && totalStock > parseFloat(item.max_stock_level);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-medium">Stock Summary</CardTitle>
          <div className="flex items-center gap-2">
            {isLowStock && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" />
                Low Stock
              </Badge>
            )}
            {isOverStock && (
              <Badge variant="secondary" className="gap-1">
                <AlertTriangle className="h-3 w-3" />
                Over Stock
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Total Stock Display */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Package className="h-5 w-5 text-gray-600" />
              <div>
                <p className="text-sm text-gray-500">Total Stock</p>
                <p className="text-2xl font-semibold">
                  {totalStock.toFixed(2)} {item?.unit?.short_name || ''}
                </p>
              </div>
            </div>
          </div>

          {/* Stock by Godown */}
          {stockSummary && stockSummary.stock_by_godown.length > 0 ? (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Stock by Location</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Godown</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-900">Quantity</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-900">Percentage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {stockSummary.stock_by_godown.map((godownStock) => {
                      const quantity = parseFloat(godownStock.quantity);
                      const percentage = totalStock > 0 ? (quantity / totalStock) * 100 : 0;
                      
                      return (
                        <tr key={godownStock.godown_id}>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {godownStock.godown_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            {quantity.toFixed(2)} {item?.unit?.short_name || ''}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 text-right">
                            {percentage.toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <Alert>
              <AlertDescription>
                No stock information available for this item.
              </AlertDescription>
            </Alert>
          )}

          {/* Stock Levels Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
            <div className="p-3 border rounded-lg">
              <p className="text-sm text-gray-500">Min Stock</p>
              <p className="text-lg font-medium">
                {item?.min_stock_level ? parseFloat(item.min_stock_level).toFixed(2) : 'Not set'}
              </p>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="text-sm text-gray-500">Max Stock</p>
              <p className="text-lg font-medium">
                {item?.max_stock_level ? parseFloat(item.max_stock_level).toFixed(2) : 'Not set'}
              </p>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="text-sm text-gray-500">Reorder Level</p>
              <p className="text-lg font-medium">
                {item?.reorder_level ? parseFloat(item.reorder_level).toFixed(2) : 'Not set'}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}